import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export type FileSizeMeterTier = 'blue' | 'amber' | 'red';

const SEGMENT_COUNT = 5;
const AMBER_THRESHOLD_PERCENT = 75;
const RED_THRESHOLD_PERCENT = 100;
const BYTES_PER_MB = 1024 * 1024;

/**
 * Pure, presentational capacity meter: shows how much of an allowed byte quota has been
 * used so far. Intentionally has no knowledge of files, uploads, or any other component
 * state — bytes in, meter out. Never represents in-flight upload progress.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-cfi-file-size-meter',
  standalone: true,
  imports: [],
  styleUrl: './cfi-file-size-meter.component.scss',
  templateUrl: './cfi-file-size-meter.component.html'
})
export class CfiFileSizeMeterComponent {
  @Input() usedBytes = 0;
  @Input() maxBytes = 0;

  public readonly segments: number[] = Array.from({ length: SEGMENT_COUNT }, (_, i) => i);

  public percent(): number {
    if (!this.maxBytes || this.maxBytes <= 0) {
      return 0;
    }
    return Math.min((this.usedBytes / this.maxBytes) * 100, 100);
  }

  public filledSegments(): number {
    const percent = this.percent();
    if (percent <= 0) {
      return 0;
    }
    return Math.min(SEGMENT_COUNT, Math.ceil(percent / (100 / SEGMENT_COUNT)));
  }

  public tier(): FileSizeMeterTier {
    const percent = this.percent();
    if (percent >= RED_THRESHOLD_PERCENT) {
      return 'red';
    }
    if (percent >= AMBER_THRESHOLD_PERCENT) {
      return 'amber';
    }
    return 'blue';
  }

  public usedMb(): string {
    return this.formatMb(this.usedBytes);
  }

  public maxMb(): string {
    return this.formatMb(this.maxBytes);
  }

  public ariaLabel(): string {
    return `Size used: ${this.usedMb()}mb of ${this.maxMb()}mb`;
  }

  private formatMb(bytes: number): string {
    if (!bytes || bytes <= 0) {
      return '0';
    }
    return Number.parseFloat((bytes / BYTES_PER_MB).toFixed(1)).toString();
  }
}
